import { RequestsApiService } from '@common/ApiService';
import paginationHeaders from '@/src/utils/pagination-headers';
import isCanceledRequestError from '@utils/is-canceled-request-error';
import updateBulkAssignmentState from '@utils/update-bulk-assignment-state';
import { SET_LAST_REFRESH_TIME, SET_REFRESH_POPUP_DISMISS_TIME } from '@mutations';
const REQUESTS_SUCCESS = 'REQUESTS_SUCCESS';
const UPDATE_REQUEST_SUCCESS = 'UPDATE_REQUEST_SUCCESS';
const REQUESTS_ERROR = 'REQUESTS_ERROR';
const REQUESTS_PROGRESS = 'REQUESTS_PROGRESS';
const REQUESTS_CLEAR = 'REQUESTS_CLEAR';
const UPDATE_PAGINATION = 'UPDATE_PAGINATION';
const UPDATE_PAGE = 'UPDATE_PAGE';
const UPDATE_BULK_REQUESTS_SUCCESS = 'UPDATE_BULK_REQUESTS_SUCCESS';
const GET_AVAILABLE_USERS_SUCCESS = 'GET_AVAILABLE_USERS_SUCCESS';
const GET_GROUPED_AVAILABLE_USERS_PROGRESS = 'GET_GROUPED_AVAILABLE_USERS_PROGRESS';
const GET_GROUPED_AVAILABLE_USERS_SUCCESS = 'GET_GROUPED_AVAILABLE_USERS_SUCCESS';
const CLEAR_GROUPED_AVAILABLE_USERS = 'CLEAR_GROUPED_AVAILABLE_USERS';
const DASHBOARD_SHOW_SUCCESS = 'DASHBOARD_SHOW_SUCCESS';
const SORT_REQUEST_LIST = 'SORT_REQUEST_LIST';
let activeDashboardFetchController = null;
const hasDifferentAssignee = (currentAssignee, nextAssignee) => {
    if (!currentAssignee || !nextAssignee)
        return false;
    if (currentAssignee.id && nextAssignee.id) {
        return currentAssignee.id !== nextAssignee.id;
    }
    return currentAssignee.displayed_name !== nextAssignee.displayed_name;
};
export const state = {
    requests: [],
    pagination: {},
    errors: {},
    progress: '',
    availableUsersToAssign: [],
    groupedAvailableUsersToAssign: {
        recent_staff: [],
        all_staff: [],
    },
    groupedAvailableUsersToAssignRequestId: null,
    groupedAvailableUsersToAssignLoading: false,
    lastRefreshTime: null,
    refreshPopupDismissTime: null,
    requestRecord: null,
    isRequestListSorted: false,
};
export const getters = {};
export const mutations = {
    [REQUESTS_SUCCESS](state, data) {
        state.requests = data;
        return state;
    },
    [SORT_REQUEST_LIST](state, data) {
        state.isRequestListSorted = data;
        return state;
    },
    [UPDATE_REQUEST_SUCCESS](state, data) {
        state.requests.forEach((request, index) => {
            if (request.id === data.id) {
                state.requests[index] = data;
                return;
            }
            if (data.booking_ref && request.booking_ref === data.booking_ref && data.booking_status) {
                state.requests[index] = { ...request, booking_status: data.booking_status };
            }
        });
    },
    [UPDATE_BULK_REQUESTS_SUCCESS](state, data) {
        updateBulkAssignmentState(state.requests, data);
    },
    [DASHBOARD_SHOW_SUCCESS](state, data) {
        state.requests.forEach((request, index) => {
            if (request.id === data.id) {
                const assignedInMeantime = !request.assignee && data.assignee;
                const unassignedInMeantime = request.assignee && !data.assignee;
                const reassignedInMeantime = hasDifferentAssignee(request.assignee, data.assignee);
                const assignmentChangedInMeantime = assignedInMeantime || unassignedInMeantime || reassignedInMeantime;
                if (assignmentChangedInMeantime) {
                    const newStatus = unassignedInMeantime ? 'unassigned' : 'assigned';
                    data['status_changed_in_meantime'] = true;
                    data['new_status'] = newStatus;
                }
                state.requests[index] = data;
            }
        });
    },
    [GET_AVAILABLE_USERS_SUCCESS](state, data) {
        state.availableUsersToAssign = data;
        return state;
    },
    [GET_GROUPED_AVAILABLE_USERS_PROGRESS](state, { requestId, isLoading }) {
        if (isLoading && state.groupedAvailableUsersToAssignRequestId !== requestId) {
            state.groupedAvailableUsersToAssign = { recent_staff: [], all_staff: [] };
        }
        if (isLoading || state.groupedAvailableUsersToAssignRequestId === requestId) {
            state.groupedAvailableUsersToAssignRequestId = requestId;
            state.groupedAvailableUsersToAssignLoading = isLoading;
        }
        return state;
    },
    [GET_GROUPED_AVAILABLE_USERS_SUCCESS](state, { requestId, data }) {
        if (state.groupedAvailableUsersToAssignRequestId !== requestId)
            return state;
        state.groupedAvailableUsersToAssign = {
            recent_staff: data?.recent_staff || [],
            all_staff: data?.all_staff || [],
        };
        return state;
    },
    [CLEAR_GROUPED_AVAILABLE_USERS](state) {
        state.groupedAvailableUsersToAssign = { recent_staff: [], all_staff: [] };
        state.groupedAvailableUsersToAssignRequestId = null;
        state.groupedAvailableUsersToAssignLoading = false;
        return state;
    },
    [UPDATE_PAGINATION](state, response) {
        state.pagination = paginationHeaders(response);
        return state;
    },
    [REQUESTS_PROGRESS](state, progress) {
        state.progress = progress;
        return state.progress;
    },
    [UPDATE_PAGE](state, page) {
        state.pagination = { ...state.pagination, page };
        return state.pagination;
    },
    [REQUESTS_ERROR](state, data) {
        state.errors = data;
        state.progress = 'error';
        return state;
    },
    [REQUESTS_CLEAR](state) {
        state.requests = {};
        return state;
    },
    [SET_LAST_REFRESH_TIME](state) {
        state.lastRefreshTime = new Date();
    },
    [SET_REFRESH_POPUP_DISMISS_TIME](state) {
        state.refreshPopupDismissTime = new Date();
    },
};
export const actions = {
    async fetchRequests({ commit }, query) {
        activeDashboardFetchController?.abort();
        const controller = new AbortController();
        activeDashboardFetchController = controller;
        query.per_page = 50;
        if (query.page) {
            commit(UPDATE_PAGE, query.page);
        }
        commit(REQUESTS_PROGRESS, 'search_loading');
        try {
            const response = await RequestsApiService.query(query, { signal: controller.signal });
            commit(REQUESTS_SUCCESS, response.data);
            commit(UPDATE_PAGINATION, response);
            commit(REQUESTS_PROGRESS, 'requests_success');
            commit(SET_LAST_REFRESH_TIME);
            commit(SET_REFRESH_POPUP_DISMISS_TIME);
            return response.data;
        }
        catch (error) {
            if (isCanceledRequestError(error)) {
                return null;
            }
            commit(REQUESTS_ERROR, error);
            throw error;
        }
        finally {
            if (activeDashboardFetchController === controller) {
                activeDashboardFetchController = null;
            }
        }
    },
    setRequests({ commit }, data) {
        commit(SORT_REQUEST_LIST, true);
        commit(REQUESTS_SUCCESS, data);
    },
    isRequestListSorted({ commit }, data) {
        commit(SORT_REQUEST_LIST, data);
    },
    show({ commit }, id) {
        return new Promise((resolve) => {
            return RequestsApiService.show(id)
                .then((response) => {
                commit(DASHBOARD_SHOW_SUCCESS, response.data);
                resolve(response.data);
            })
                .catch((error) => commit(REQUESTS_ERROR, error));
        });
    },
    assign({ commit }, params) {
        return new Promise((resolve) => {
            return RequestsApiService.assign(params)
                .then((response) => {
                commit(UPDATE_REQUEST_SUCCESS, response.data);
                resolve(response.data);
            })
                .catch((error) => commit(REQUESTS_ERROR, error));
        });
    },
    unassign({ commit }, params) {
        const { skipCommit, ...requestParams } = params;
        return new Promise((resolve) => {
            return RequestsApiService.unassign(requestParams)
                .then((response) => {
                if (!skipCommit) {
                    commit(UPDATE_REQUEST_SUCCESS, response.data);
                }
                resolve(response.data);
            })
                .catch((error) => commit(REQUESTS_ERROR, error));
        });
    },
    async bulkAssign({ commit }, params) {
        const { confirmedBookingRefs = [], ...requestParams } = params;
        try {
            const response = await RequestsApiService.bulkAssign(requestParams);
            commit(UPDATE_BULK_REQUESTS_SUCCESS, {
                ...response.data,
                confirmedBookingRefs,
            });
            return response.data;
        }
        catch (error) {
            commit(REQUESTS_ERROR, error);
            throw error;
        }
    },
    async bulkUnassign({ commit }, params) {
        try {
            const response = await RequestsApiService.bulkUnassign(params);
            commit(UPDATE_BULK_REQUESTS_SUCCESS, response.data);
            return response.data;
        }
        catch (error) {
            commit(REQUESTS_ERROR, error);
            throw error;
        }
    },
    getAvailableUsersToAssign({ commit }, params) {
        return RequestsApiService.getAvailableUsersToAssign(params)
            .then((response) => {
            commit(GET_AVAILABLE_USERS_SUCCESS, response.data);
            return response.data;
        })
            .catch((error) => {
            commit(REQUESTS_ERROR, error);
            throw error;
        });
    },
    async getGroupedAvailableUsersToAssign({ commit }, params) {
        const requestId = params.request_id;
        commit(GET_GROUPED_AVAILABLE_USERS_PROGRESS, { requestId, isLoading: true });
        try {
            const response = await RequestsApiService.getGroupedAvailableUsersToAssign(params);
            commit(GET_GROUPED_AVAILABLE_USERS_SUCCESS, { requestId, data: response.data });
            return response.data;
        }
        catch (error) {
            commit(GET_GROUPED_AVAILABLE_USERS_SUCCESS, { requestId, data: {} });
            commit(REQUESTS_ERROR, error);
            throw error;
        }
        finally {
            commit(GET_GROUPED_AVAILABLE_USERS_PROGRESS, { requestId, isLoading: false });
        }
    },
    clearGroupedAvailableUsersToAssign({ commit }) {
        commit(CLEAR_GROUPED_AVAILABLE_USERS);
    },
    async getAssignmentConflict({ commit }, params) {
        try {
            const response = await RequestsApiService.getAssignmentConflict(params);
            return response.data;
        }
        catch (error) {
            commit(REQUESTS_ERROR, error);
            throw error;
        }
    },
    async getBulkAssignmentConflict({ commit }, params) {
        try {
            const response = await RequestsApiService.getBulkAssignmentConflict(params);
            return response.data;
        }
        catch (error) {
            commit(REQUESTS_ERROR, error);
            throw error;
        }
    },
    getPassengerAccessConsideration({ commit }, params) {
        return new Promise((resolve) => {
            return RequestsApiService.getPassengerAccessConsideration(params)
                .then((response) => {
                resolve(response.data);
            })
                .catch((error) => commit(REQUESTS_ERROR, error));
        });
    },
    getPassengerJourneyAssistances({ commit }, params) {
        return new Promise((resolve) => {
            return RequestsApiService.getPassengerJourneyAssistances(params)
                .then((response) => {
                resolve(response.data);
            })
                .catch((error) => commit(REQUESTS_ERROR, error));
        });
    },
    updateRequest({ commit }, item) {
        return commit(UPDATE_REQUEST_SUCCESS, item);
    },
};
